import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

type FeedbackConfig = {
  enabled: boolean;
};

let cached: FeedbackConfig | null = null;
let fetchPromise: Promise<FeedbackConfig> | null = null;

async function fetchFeedbackConfig(): Promise<FeedbackConfig> {
  if (cached) return cached;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    const { data, error } = await api.get<FeedbackConfig>('/feedback/config');
    const result: FeedbackConfig = {
      enabled: !error && !!data?.enabled,
    };
    cached = result;
    return result;
  })();

  try {
    return await fetchPromise;
  } finally {
    fetchPromise = null;
  }
}

/** Reset module cache (e.g. after logout). */
export function resetFeedbackConfigCache(): void {
  cached = null;
  fetchPromise = null;
}

export function useFeedbackConfig(): { enabled: boolean; isLoading: boolean } {
  const { isAuthenticated } = useAuth();
  const [enabled, setEnabled] = useState(cached?.enabled ?? false);
  const [isLoading, setIsLoading] = useState(isAuthenticated && cached === null);

  useEffect(() => {
    if (!isAuthenticated) {
      resetFeedbackConfigCache();
      setEnabled(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(cached === null);

    void fetchFeedbackConfig().then((cfg) => {
      if (!cancelled) {
        setEnabled(cfg.enabled);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return { enabled, isLoading };
}
